import React, { useContext, useEffect, useState } from 'react';
import { Spin, Tree } from 'antd';

import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import {
  emptyId,
  GetNextPermissionsGroup,
  hasDataAccessPermissionsTo,
} from '@totem/components/userContext/security/userUtilities';
import UserDetailsContext from '@totem/components/users/details/UserDetailsContext';
import { OrganizationHierarchy } from '@totem/types/organization';
import {
  DataAccessGroup,
  DataAccessItem,
  DataAccessPermissionUpdate,
} from '@totem/types/user';

const UserDataPermissions = () => {
  const {
    user,
    dataPermissions,
    allDataPermissions,
    dataPermissionsHierarchy,
    dataLoading,
    onDataPermissionUpdate,
  } = useContext(UserDetailsContext);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<React.Key[]>([]);

  // --- Tree data builders ---

  const buildControlSystems = (
    parentKey: string,
    _controlSystemRestrictions: DataAccessItem[],
    controlSystemHierarchy: OrganizationHierarchy[],
    regionId: string,
    buildingId: string,
  ) => {
    return controlSystemHierarchy.map((controlSystem) => {
      const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
        regionId,
        buildingId,
        controlSystemId: controlSystem.id,
      });
      return {
        title: controlSystem.name,
        key: `${parentKey}-C:${controlSystem.id}`,
        checkable: true,
        disabled: !canManage,
      };
    });
  };

  const buildStaticControlSystemOptions = (
    parentKey: string,
    building: DataAccessItem,
    buildingHierarchy: OrganizationHierarchy,
    regionId: string,
  ) => {
    if (typeof building === 'undefined') return [];

    const controlSystemGroup = building.children.find(
      (grp) => grp.name === 'Control System' || grp.name === 'ControlSystem',
    );
    if (typeof controlSystemGroup === 'undefined') return [];

    const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
      regionId,
      buildingId: building.id,
      controlSystemId: emptyId,
    });

    let controlSystems = [];
    if (
      controlSystemGroup.accessType !== 'All' &&
      controlSystemGroup.accessType !== 'None'
    ) {
      controlSystems = buildControlSystems(
        parentKey,
        controlSystemGroup.restrictedTo,
        buildingHierarchy.children,
        regionId,
        building.id,
      );
    }

    return [
      {
        title: 'All Control Systems',
        key: `${parentKey}:C:All`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'No Control Systems',
        key: `${parentKey}:C:None`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'Selected Control Systems',
        key: `${parentKey}:C:Partial`,
        checkable: true,
        children: controlSystems,
        disabled: false,
      },
    ];
  };

  const buildBuildings = (
    parentKey: string,
    buildingRestrictions: DataAccessItem[],
    buildingHierarchy: OrganizationHierarchy[],
    regionId: string,
  ) => {
    return buildingHierarchy.map((buildingItem) => {
      const buildingPermission = buildingRestrictions.find(
        (reg) => reg.id === buildingItem.id,
      );
      const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
        regionId,
        buildingId: buildingItem.id,
        controlSystemId: emptyId,
      });
      return {
        title: buildingItem.name,
        key: `${parentKey}-B:${buildingItem.id}`,
        checkable: true,
        disabled: !canManage,
        children: buildStaticControlSystemOptions(
          `${parentKey}-B:${buildingItem.id}`,
          buildingPermission,
          buildingItem,
          regionId,
        ),
      };
    });
  };

  const buildStaticRegionOptions = (
    parentKey: string,
    region: DataAccessItem,
    regionHierarchy: OrganizationHierarchy,
  ) => {
    if (typeof region === 'undefined' || region === null) return [];

    const buildingGroup = region.children.find(
      (grp) => grp.name === 'Building',
    );
    if (typeof buildingGroup === 'undefined') return [];

    const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
      regionId: region.id,
      buildingId: emptyId,
      controlSystemId: emptyId,
    });

    let buildings = [];
    if (
      buildingGroup.accessType !== 'All' &&
      buildingGroup.accessType !== 'None'
    ) {
      buildings = buildBuildings(
        parentKey,
        buildingGroup.restrictedTo,
        regionHierarchy.children,
        region.id,
      );
    }

    return [
      {
        title: 'All Buildings',
        key: `${parentKey}:B:All`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'No Buildings',
        key: `${parentKey}:B:None`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'Selected Buildings',
        key: `${parentKey}:B:Partial`,
        checkable: true,
        children: buildings,
        disabled: false,
      },
    ];
  };

  const buildRegions = (
    parentKey: string,
    regionRestrictions: DataAccessItem[],
    regionHierarchy: OrganizationHierarchy[],
  ) => {
    return regionHierarchy.map((regionItem) => {
      const regionPermission = regionRestrictions.find(
        (reg) => reg.id === regionItem.id,
      );
      const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
        regionId: regionItem.id,
        buildingId: emptyId,
        controlSystemId: emptyId,
      });
      return {
        title: regionItem.name,
        key: `${parentKey}-R:${regionItem.id}`,
        checkable: true,
        disabled: !canManage,
        children: buildStaticRegionOptions(
          `${parentKey}-R:${regionItem.id}`,
          regionPermission,
          regionItem,
        ),
      };
    });
  };

  const buildStaticOrganizationOptions = (
    parentKey: string,
    regions: OrganizationHierarchy[],
  ) => {
    let regionGroup: DataAccessGroup;
    if (typeof dataPermissions !== 'undefined' && dataPermissions !== null) {
      regionGroup = dataPermissions.find((grp) => grp.name === 'Region');
    }

    const canManage = hasDataAccessPermissionsTo(allDataPermissions, {
      regionId: emptyId,
      buildingId: emptyId,
      controlSystemId: emptyId,
    });

    let regionList = [];
    if (
      typeof regionGroup !== 'undefined' &&
      regionGroup.accessType !== 'All' &&
      regionGroup.accessType !== 'None'
    ) {
      regionList = buildRegions(parentKey, regionGroup.restrictedTo, regions);
    }

    return [
      {
        title: 'All Regions',
        key: `${parentKey}:R:All`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'No Regions',
        key: `${parentKey}:R:None`,
        checkable: true,
        disabled: !canManage,
      },
      {
        title: 'Selected Regions',
        key: `${parentKey}:R:Partial`,
        checkable: true,
        disabled: false,
        children: regionList,
      },
    ];
  };

  const buildTreeData = () => {
    if (
      typeof dataPermissionsHierarchy === 'undefined' ||
      dataPermissionsHierarchy === null
    ) {
      return [];
    }
    return [
      {
        title: dataPermissionsHierarchy.name,
        key: `O:${dataPermissionsHierarchy.id}`,
        checkable: false,
        children: buildStaticOrganizationOptions(
          `O:${dataPermissionsHierarchy.id}`,
          dataPermissionsHierarchy.children,
        ),
      },
    ];
  };

  // --- Checked key helpers ---

  const buildSelectedKeysForGroup = (
    keys: string[],
    parentKey: string,
    currentGroup: string,
    group: DataAccessGroup,
  ) => {
    const keyPrefix = group.name.charAt(0);

    switch (group.accessType) {
      case 'All':
        keys.push(`${parentKey}:${keyPrefix}:All`);
        break;
      case 'None':
        keys.push(`${parentKey}:${keyPrefix}:None`);
        break;
      default:
        keys.push(`${parentKey}:${keyPrefix}:Partial`);
        break;
    }

    group.restrictedTo.forEach((perm) => {
      keys.push(`${parentKey}-${keyPrefix}:${perm.id}`);
      const nextGroupAttributes = GetNextPermissionsGroup(currentGroup);
      const nextGroup = perm.children.find(
        (grp) => grp.name === nextGroupAttributes.key,
      );
      if (typeof nextGroup !== 'undefined') {
        buildSelectedKeysForGroup(
          keys,
          `${parentKey}-${keyPrefix}:${perm.id}`,
          nextGroupAttributes.key,
          nextGroup,
        );
      }
    });
  };

  const buildSelectedKeys = () => {
    const keys: string[] = [];
    if (
      typeof dataPermissions === 'undefined' ||
      dataPermissions === null ||
      typeof dataPermissionsHierarchy === 'undefined' ||
      dataPermissionsHierarchy === null
    ) {
      return keys;
    }

    const regionGroup = dataPermissions.find((grp) => grp.name === 'Region');
    if (typeof regionGroup !== 'undefined') {
      buildSelectedKeysForGroup(
        keys,
        `O:${dataPermissionsHierarchy.id}`,
        'Region',
        regionGroup,
      );
    } else {
      keys.push(`O:${dataPermissionsHierarchy.id}:R:All`);
    }
    return keys;
  };

  // --- Initial expansion: top level + any "Selected..." nodes and their ancestors ---

  const buildInitialExpandedKeys = (): React.Key[] => {
    if (!dataPermissionsHierarchy) return [];

    const expanded = new Set<string>();
    expanded.add(`O:${dataPermissionsHierarchy.id}`);

    const expandEntityAndAncestors = (entityKey: string) => {
      const match = entityKey.match(/^(.+)-([RBC]):[^:-]+$/);
      if (match) {
        const parentEntity = match[1];
        const typeLetter = match[2];
        expanded.add(`${parentEntity}:${typeLetter}:Partial`);

        expanded.add(parentEntity);

        expandEntityAndAncestors(parentEntity);
      }
    };

    buildSelectedKeys().forEach((key) => {
      const keyStr = key as string;
      const partialMatch = keyStr.match(/^(.+):([RBC]):Partial$/);
      if (partialMatch) {
        expanded.add(keyStr);

        expanded.add(partialMatch[1]);

        expandEntityAndAncestors(partialMatch[1]);
      } else {
        expandEntityAndAncestors(keyStr);
      }
    });

    return Array.from(expanded);
  };

  useEffect(() => {
    if (dataPermissionsHierarchy) {
      setTreeExpandedKeys(buildInitialExpandedKeys());
    }
  }, [dataPermissions, dataPermissionsHierarchy]);

  // --- Update handler ---

  const buildPermissionUpdateFromTree = (
    info: any,
  ): DataAccessPermissionUpdate => {
    const nodeInfo = {
      key: info.node.key as string,
      name: info.node.title as string,
      isChecked: info.checked as boolean,
    };

    const update: DataAccessPermissionUpdate = {
      userId: user.id,
      updateType: '',
      action: '',
      data: {
        organizationId: '',
        regionOption: '',
        regionId: '',
        regionName: '',
        buildingOption: '',
        buildingId: '',
        buildingName: '',
        controlSystemOption: '',
        controlSystemId: '',
        controlSystemName: '',
      },
    };

    const keyParts = nodeInfo.key.split('-');
    const keysArray = keyParts.map((part) => part.split(':'));
    const lastKey = keysArray[keysArray.length - 1];

    if (lastKey.length === 4) {
      update.action = 'UPDATE';
      switch (lastKey[0]) {
        case 'O':
          update.updateType = 'REGION_OPTION_UPDATE';

          update.data.organizationId = lastKey[1];

          update.data.regionOption = lastKey[3];
          break;
        case 'R':
          update.updateType = 'BUILDING_OPTION_UPDATE';

          update.data.organizationId = keysArray[0][1];

          update.data.regionId = lastKey[1];

          update.data.buildingOption = lastKey[3];
          break;
        case 'B':
          update.updateType = 'CONTROL_SYSTEM_OPTION_UPDATE';

          update.data.organizationId = keysArray[0][1];

          update.data.regionId = keysArray[1][1];

          update.data.buildingId = lastKey[1];

          update.data.controlSystemOption = lastKey[3];
          break;
        default:
          break;
      }
    } else if (lastKey.length === 2) {
      update.action = nodeInfo.isChecked ? 'ADD' : 'DELETE';
      switch (lastKey[0]) {
        case 'R':
          update.updateType = 'REGION_UPDATE';

          update.data.organizationId = keysArray[0][1];

          update.data.regionId = lastKey[1];

          update.data.regionName = nodeInfo.name;
          break;
        case 'B':
          update.updateType = 'BUILDING_UPDATE';

          update.data.organizationId = keysArray[0][1];

          update.data.regionId = keysArray[1][1];

          update.data.buildingId = lastKey[1];

          update.data.buildingName = nodeInfo.name;
          break;
        case 'C':
          update.updateType = 'CONTROL_SYSTEM_UPDATE';

          update.data.organizationId = keysArray[0][1];

          update.data.regionId = keysArray[1][1];

          update.data.buildingId = keysArray[2][1];

          update.data.controlSystemId = lastKey[1];

          update.data.controlSystemName = nodeInfo.name;
          break;
        default:
          break;
      }
    }

    return update;
  };

  const handleTreeItemChecked = (_checkedKeys: React.Key[], info: any) => {
    const update = buildPermissionUpdateFromTree(info);
    if (update.updateType !== '') {
      onDataPermissionUpdate(update);
    }
  };

  return (
    <IBCard isCovered title="Data Permissions">
      {dataLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <Spin />
        </div>
      ) : (
        <Tree
          checkable
          checkStrictly
          height={400}
          defaultExpandParent
          expandedKeys={treeExpandedKeys}
          checkedKeys={buildSelectedKeys()}
          treeData={buildTreeData()}
          onCheck={handleTreeItemChecked}
          onExpand={(keys) => setTreeExpandedKeys(keys)}
        />
      )}
    </IBCard>
  );
};

export default UserDataPermissions;
